import { Component, ElementRef, OnInit, ViewChild, inject, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FiltersService } from 'src/app/core/services/filters/filters.service';
import { FilterUtilService } from 'src/app/core/services/uitls/filter-utilizer/filter-util.service';
import { QuestionsService } from '../../services/questions.service';
import { QUESTION_STATUS_LABELS, Question, QuestionStatus } from '../../models/question.model';

interface PaginationConfig {
  id: string;
  itemsPerPage: number;
  currentPage: number;
  totalItems: number;
}

@Component({
  selector: 'app-questions',
  standalone: false,
  templateUrl: './questions.component.html',
  styleUrls: ['./questions.component.scss'],
})
export class QuestionsComponent implements OnInit, OnDestroy {
  spinner: boolean = false;
  searchValue: string = '';
  page: number = 1;
  page_size: number = 20;
  totalRecords: number = 0;
  config!: PaginationConfig;
  filterPage: string = 'QUESTIONS';
  pageTitle: string = 'الاسئلة';
  filterData: unknown;
  filterDataInit: Record<string, unknown> = {};
  filterParamsUrl: Record<string, string | string[]> = {};
  questions: Question[] = [];
  currentItemsPerPage: number = 0;
  spinnerKey: string = 'spinner';
  destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  
  // Tab filters
  selectedTab: string = 'all';
  tabCounts = {
    all: 0,
    open: 0,
    needsReview: 0,
    closed: 0
  };

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private titleService = inject(Title);
  private questionsService = inject(QuestionsService);
  private filter = inject(FiltersService);
  private filterUtil = inject(FilterUtilService);

  @ViewChild('goUp', { static: true }) contentPage!: ElementRef;

  ngOnInit(): void {
    this.titleService.setTitle(this.pageTitle + ' | عاين');

    const tabParam = this.route.snapshot.queryParamMap.get('tab');
    if (tabParam && ['all', 'open', 'needsReview', 'closed'].includes(tabParam)) {
      this.selectedTab = tabParam;
    }

    this.config = {
      id: 'questionsPagination',
      itemsPerPage: this.page_size,
      currentPage: this.page,
      totalItems: this.totalRecords,
    };

    this.getFilterData();
    this.filter.changeSelectedFilter(this.filterDataInit);
    this.filterUtil.applySelectedFilter(this, this.loadData, this.spinnerKey);
    this.loadTabCounts();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.filter.changeSelectedFilter({});
  }

  getFilterData(): void {
    this.filter.getFiltersData({ filter_page: this.filterPage }).subscribe({
      next: (data) => { this.filterData = data; },
      error: () => { /* Filter data loading failed silently */ },
    });
  }

  loadData(): void {
    this.spinner = true;
    const payload: Record<string, string | number | string[]> = {
      page: this.config.currentPage,
      page_size: this.config.itemsPerPage,
      ...this.filterParamsUrl,
    };

    // Add status filter based on selected tab
    if (this.selectedTab !== 'all') {
      payload['status'] = this.getStatusValue(this.selectedTab);
    }

    // Add search query
    if (this.searchValue) {
      payload['query'] = this.searchValue;
    }

    this.questionsService.getQuestions(payload)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          this.questions = response.data || [];
          this.totalRecords = response.meta?.pagination?.count || 0;
          this.config.totalItems = this.totalRecords;
          
          this.spinner = false;
        },
        error: (error) => {
          console.error('Error loading questions:', error);
          this.spinner = false;
          this.questions = [];
        },
      });
  }

  getStatusValue(tab: string): string {
    const statusMap: Record<string, QuestionStatus> = {
      'open': QuestionStatus.Open,
      'needsReview': QuestionStatus.NeedReview,
      'closed': QuestionStatus.Closed,
    };
    return statusMap[tab] || tab;
  }

  loadTabCounts(): void {
    const basePayload: Record<string, string | number> = {
      page: 1,
      page_size: 1,
      ...this.filterParamsUrl,
    };

    if (this.searchValue) {
      basePayload['query'] = this.searchValue;
    }

    // Load count for all questions
    this.questionsService.getQuestions(basePayload)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          this.tabCounts.all = response.meta?.pagination?.count || 0;
        },
        error: () => { this.tabCounts.all = 0; }
      });

    // Load count for open questions
    this.questionsService.getQuestions({ ...basePayload, status: QuestionStatus.Open })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          this.tabCounts.open = response.meta?.pagination?.count || 0;
        },
        error: () => { this.tabCounts.open = 0; }
      });

    // Load count for needs review questions
    this.questionsService.getQuestions({ ...basePayload, status: QuestionStatus.NeedReview })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          this.tabCounts.needsReview = response.meta?.pagination?.count || 0;
        },
        error: () => { this.tabCounts.needsReview = 0; }
      });

    // Load count for closed questions
    this.questionsService.getQuestions({ ...basePayload, status: QuestionStatus.Closed })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          this.tabCounts.closed = response.meta?.pagination?.count || 0;
        },
        error: () => { this.tabCounts.closed = 0; }
      });
  }

  selectTab(tab: string): void {
    this.selectedTab = tab;
    this.resetPagination();
    this.updateUrlParams();
    this.loadData();
  }

  filterFormValues(event: { filterParamsUrl: Record<string, string | string[]> }): void {
    this.spinner = true;
    this.filterParamsUrl = event.filterParamsUrl;
    this.resetPagination();
    this.loadData();
    this.loadTabCounts();
  }

  quickSearch(searchValue: string): void {
    this.searchValue = searchValue || '';
    this.resetPagination();
    this.updateUrlParams();
    this.loadData();
    this.loadTabCounts();
  }

  pageChanged(event: { currentPage?: number } | number): void {
    this.config.currentPage = typeof event === 'number' ? event : (event.currentPage ?? this.config.currentPage);
    this.updateUrlParams();
    this.loadData();
    this.contentPage.nativeElement.scrollIntoView({ behavior: 'smooth' });
  }

  pageSizeChanged(size: number): void {
    this.config.itemsPerPage = size;
    this.config.currentPage = 1;
    this.updateUrlParams();
    this.loadData();
  }

  private resetPagination(): void {
    this.config.currentPage = 1;
  }

  private updateUrlParams(): void {
    const queryParams: Record<string, string | number | string[]> = {
      page: this.config.currentPage,
      page_size: this.config.itemsPerPage,
      ...this.filterParamsUrl,
    };

    if (this.selectedTab !== 'all') {
      queryParams['status'] = this.getStatusValue(this.selectedTab);
    }

    if (this.searchValue) {
      queryParams['query'] = this.searchValue;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: '',
    });
  }

  editQuestion(questionId: number): void {
    this.router.navigate(['/pages/ask-ayen/question', questionId]);
  }

  getStatusClass(status?: string): string {
    if (!status) return '';
    const statusLower = status.toLowerCase();

    if (statusLower === QuestionStatus.NeedReview || statusLower.includes('review') || statusLower.includes('مراجعة')) {
      return 'needs-review-status';
    } else if (statusLower === QuestionStatus.Open || statusLower.includes('مفتوح')) {
      return 'open-status';
    } else if (statusLower === QuestionStatus.Closed || statusLower.includes('مغلق')) {
      return 'closed-status';
    }
    return '';
  }

  getStatusText(status?: string): string {
    if (!status) return '';
    const statusLower = status.toLowerCase() as QuestionStatus;
    return QUESTION_STATUS_LABELS[statusLower] || status;
  }

  getClientName(question: Question): string {
    if (!question?.client) return '-';
    const firstName = question.client.first_name || '';
    const lastName = question.client.last_name || '';
    return `${firstName} ${lastName}`.trim() || '-';
  }

  getSpecialization(question: Question): string {
    return question?.specialization?.name_ar || '-';
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    // The date is already formatted from the API as "8/3/2026 15:05:17"
    // Extract just the date part
    return dateString.split(' ')[0] || dateString;
  }
}
